import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { HttpVerb } from '../../../../../types/api-types'
import { FormDelegate, PageNavigation, WellMode } from '../../../../../types/routing-types'
import ConfirmTransition from '../../../../components/confirm-transition'
import { ERROR_MESSAGES } from '../../../../components/errors/messages'
import WellDetailsConsentEdit from '../../../../components/well-forms/details/well-details-consent-edit'
import WellDetailsDrillingEdit from '../../../../components/well-forms/details/well-details-drilling-edit'
import WellDetailsEditButtons from '../../../../components/well-forms/details/well-details-edit-buttons'
import WellDetailsIdentifiersEdit from '../../../../components/well-forms/details/well-details-identifiers-edit'
import WellDetailsInstallationConsentView from '../../../../components/well-forms/details/well-details-installation-consent-view'
import WellDetailsLocationEdit from '../../../../components/well-forms/details/well-details-location-edit'
import NotesWrapperEdit from '../../../../components/well-forms/notes/notes-wrapper-edit'
import { useWellState } from '../../../../context/well-context'
import { useGetWellData, useSaveWellData } from '../../../../hooks/use-api'
import useEditForm from '../../../../hooks/use-edit-form'
import useToastMessage from '../../../../hooks/use-toast-message'
import useUser from '../../../../hooks/use-user'
import { isDriller } from '../../../../services/user-utils'
import { useWellId } from '../../../../hooks/use-well-id'
import ViewWellDetails from '../view/details'
import { Divider, Heading, RecordContent, RecordFooter, useTitleChange } from '@msaf/core-react'

export function EditWellDetails({ setIsDirty }: FormDelegate) {
  const id = useWellId()
  const [isWellGrantedAsLegacy, setIsWellGrantedAsLegacy] = useState<boolean>(false)
  const location = useLocation() as { state: { isRefetchFromAGSUpload: boolean } }
  const {
    wellState: { isDraft },
  } = useWellState()

  const urls: Omit<PageNavigation, 'saveAndReturnRoute'> = {
    requestUrl: `wells/${id}/details/${isDraft ? 'new' : 'edit'}`, // FIXME-NWP Should be a single endpoint for both. Let the server decide.
    method: HttpVerb.PUT,
    cancelRoute: `/wells/${id}/view/details`,
    saveRouteNewId: (id) => `/wells/${id}/edit/details`,
    saveAndContinueRoute: (id) => `/wells/${id}/edit/construction`,
  }
  useTitleChange(`${id === WellMode.NEW ? 'New' : 'Edit'} Well | Details`)

  const stateQueryResult = useGetWellData(urls.requestUrl)
  const saveMutationResult = useSaveWellData(urls.requestUrl, urls.method)
  const { user } = useUser()

  const {
    fields,
    error,
    editField,
    isLoading,
    isSaveError,
    setValue,
    handleSave,
    addField,
    removeField,
    validationErrors,
    isSaved,
    refetch,
  } = useEditForm({
    stateQueryResult,
    saveMutationResult,
    setIsDirty,
  })

  useToastMessage({
    errorMessage: validationErrors ? ERROR_MESSAGES.VALIDATION_ERROR : ERROR_MESSAGES.SAVE_FAILED,
    successMessage: 'Save successful',
    error,
    isSuccess: isSaved,
    isError: isSaveError,
  })

  useEffect(() => {
    if (id === WellMode.NEW && location?.state?.isRefetchFromAGSUpload) {
      refetch?.()
    }
  }, [id, location?.state?.isRefetchFromAGSUpload, refetch])

  return (
    <>
      <RecordContent>
        <Heading level={2}>Details</Heading>
        <WellDetailsIdentifiersEdit
          isLoading={isLoading}
          fields={fields}
          validationErrors={validationErrors ?? []}
          setValue={setValue}
          addField={addField}
          removeField={removeField}
        />
        <Divider />
        <WellDetailsLocationEdit
          isLoading={isLoading}
          fields={fields}
          validationErrors={validationErrors ?? []}
          setValue={setValue}
          isWellGrantedAsLegacy={isWellGrantedAsLegacy}
        />
        <Divider />
        {isDraft && isDriller(user) ? (
          <WellDetailsInstallationConsentView isNewRecord isLoading={isLoading} fields={fields} />
        ) : (
          <WellDetailsConsentEdit
            isLoading={isLoading}
            fields={fields}
            validationErrors={validationErrors ?? []}
            setValue={setValue}
            addField={addField}
            removeField={removeField}
            setIsWellGrantedAsLegacy={setIsWellGrantedAsLegacy}
          />
        )}
        <Divider />
        <WellDetailsDrillingEdit
          isLoading={isLoading}
          fields={fields}
          validationErrors={validationErrors ?? []}
          setValue={setValue}
          isWellGrantedAsLegacy={isWellGrantedAsLegacy}
        />
      </RecordContent>
      <RecordFooter
        notes={
          <NotesWrapperEdit
            notes={fields.notes ?? []}
            isLoading={isLoading}
            addField={(note) => addField('notes', note, true)}
            removeField={(index: number) => removeField('notes', index)}
            editField={(note, idx) => editField('notes', note, idx)}
            setIsDirty={setIsDirty}
          />
        }
        areActionsFixed={true}
      >
        <WellDetailsEditButtons urls={urls} handleSave={handleSave} />
      </RecordFooter>
    </>
  )
}

export default function EditWellDetailsWrapped() {
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const { user } = useUser()
  const isDrillerUser = useMemo(() => isDriller(user), [user])
  const { wellState } = useWellState()

  return !wellState?.isDraft && isDrillerUser ? (
    <ViewWellDetails />
  ) : (
    <ConfirmTransition isFormDirty={isDirty}>
      <EditWellDetails setIsDirty={setIsDirty} />
    </ConfirmTransition>
  )
}
